import React, { useMemo } from 'react';
import { useCallback } from 'react';

import { Desktop } from 'components/Responsive';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { htmlT } from 'utils/generic';

import { Container, IconPrev, IconNext, Page, Section } from './styles';

export interface Props {
  total?: number;
  currentPage?: number;
  maxItemsPerPage?: number;
  onClick?: (page: number) => void;
}
const Pagination = ({ total, currentPage, maxItemsPerPage, onClick }: Props) => {
  const { t } = useStoreTranslation();
  if (!total || !currentPage || !maxItemsPerPage) return null;

  const totalPages = useMemo(() => Math.ceil(total / maxItemsPerPage), [total, maxItemsPerPage]);
  const prevEnabled = useMemo(() => currentPage > 1, [currentPage]);
  const nextEnabled = useMemo(() => currentPage < totalPages, [currentPage, totalPages]);

  const PageLink = useCallback(
    ({ page, isCurrent }: { page: number; isCurrent: boolean }) =>
      isCurrent ? (
        <Page>
          <Page.Current>{page}</Page.Current>
        </Page>
      ) : (
        <Page>
          <Page.Link
            onClick={() => {
              if (onClick) onClick(page);
            }}
          >
            {page}
          </Page.Link>
        </Page>
      ),
    [onClick],
  );

  const PageLoop = useCallback(
    ({ idx }: { idx: number }) => {
      const startPage = currentPage > 3 ? (currentPage < totalPages - 2 ? currentPage - 3 : totalPages - 5) : 0;
      const pageNumber = startPage + idx;
      return <PageLink key={pageNumber} page={pageNumber} isCurrent={currentPage === pageNumber} />;
    },
    [PageLink, currentPage, totalPages],
  );

  const showLast = useMemo(() => totalPages > 5 && currentPage < totalPages - 2, [totalPages, currentPage]);

  return (
    <Container>
      {totalPages > 1 ? (
        <Section>
          <Page $disabled={!prevEnabled}>
            <Page.Link
              onClick={() => {
                if (prevEnabled && onClick) onClick(currentPage - 1);
              }}
              $disabled={!prevEnabled}
            >
              <IconPrev /> <Desktop>{t('pagination.prev')}</Desktop>
            </Page.Link>
          </Page>

          {currentPage > 3 && totalPages > 5 && (
            <>
              <PageLink key={1} page={1} isCurrent={false} />
              <Page>...</Page>
            </>
          )}
          {totalPages > 4
            ? Number(5)
                .toArray()
                .map((idx) => <PageLoop key={idx} idx={idx} />)
            : Number(totalPages)
                .toArray()
                .map((idx) => <PageLoop key={idx} idx={idx} />)}
          {showLast && (
            <>
              <Page>...</Page>
              <PageLink key={totalPages} page={totalPages} isCurrent={false} />
            </>
          )}

          <Page $disabled={!nextEnabled}>
            <Page.Link
              onClick={() => {
                if (nextEnabled && onClick) onClick(currentPage + 1);
              }}
              $disabled={!nextEnabled}
            >
              <Desktop>{t('pagination.next')}</Desktop> <IconNext />
            </Page.Link>
          </Page>
        </Section>
      ) : (
        <Section></Section>
      )}
      <Section>
        <Page.Summary>{htmlT(t, 'pagination.summary', { currentPage, totalPages, total })}</Page.Summary>
      </Section>
    </Container>
  );
};

export default Pagination;
